var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Card, CardActions, CardContent, CardHeader, LinearProgress, Stack, TextField, Typography, Link, } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useFileUploader } from "../hooks/useFileUploader";
import { isVideoFile } from "../util/ImageUtils";
import { generateVideoThumbnail, getVideoMetadata, getConvertedVideoUrl, } from "../util/VideoUtils";
import { savePost, } from "../util/DataUtils";
import { AddAPhoto, AddLink, IosShare, SaveAlt } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import { ContentCard } from "./ContentCard";
import { useCacheBusterStore } from "../util/UrlUtils";
import { useLogger } from "../logger";
import { UserAvatar } from "./UserAvatar";
import { useChannelPostsStore } from "../store/ChannelPostsStore";
import { useStudioJobStore } from "../store/StudioJobStore";
import { useNavigate } from "react-router-dom";
import { getContentURL } from "../ContentAPI";
import { renderTextWithStyledUrls, getFirstUrlFromText, } from "../util/TextUtils";
const LOGPREFIX = "NewPostCard: ";
// URL detection utility function
export const extractFirstUrl = (text) => {
    if (!text)
        return null;
    // Regex to match URLs starting with http:// or https://
    const urlRegex = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;
    const matches = text.match(urlRegex);
    if (matches && matches.length > 0) {
        let url = matches[0];
        // Convert http:// to https:// to pass existing validation
        if (url.startsWith("http://")) {
            url = url.replace("http://", "https://");
        }
        return url;
    }
    return null;
};
export const NewPostCard = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const logger = useLogger((state) => state);
    const [studioJobLoaded, setStudioJobLoaded] = useState(false);
    const [studioJob, setStudioJob] = useState(undefined);
    const canvasRef = useRef(null);
    const navigate = useNavigate();
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const studioJobStore = useStudioJobStore();
    // Form state
    const [postTitle, setPostTitle] = useState(((_a = props.existingPost) === null || _a === void 0 ? void 0 : _a.title) || "");
    const [postTitleHelper, setPostTitleHelper] = useState(undefined);
    const [postSubtitle, setPostSubtitle] = useState(((_b = props.existingPost) === null || _b === void 0 ? void 0 : _b.subtitle) || "");
    const [postSubtitleHelper, setPostSubtitleHelper] = useState(undefined);
    const [postText, setPostText] = useState(((_c = props.existingPost) === null || _c === void 0 ? void 0 : _c.text) || "");
    const [postTextHelper, setPostTextHelper] = useState(undefined);
    const [postLink, setPostLink] = useState(((_d = props.existingPost) === null || _d === void 0 ? void 0 : _d.link) || "");
    const [postLinkHelper, setPostLinkHelper] = useState(undefined);
    // Draft state
    const [draftPostId, setDraftPostId] = useState((_e = props.existingPost) === null || _e === void 0 ? void 0 : _e.postId);
    // File state - now using temp files instead of draft files
    const [tempFiles, setTempFiles] = useState([]);
    const [previewImageDataUrl, setPreviewImageDataUrl] = useState("");
    const [uploadingFiles, setUploadingFiles] = useState(false);
    // Studio job state
    const [loadingStudioJob, setLoadingStudioJob] = useState(false);
    const [allFilesUploaded, setAllFilesUploaded] = useState(false);
    // UI state
    const [addLinkVisible, setAddLinkVisible] = useState(!!((_f = props.existingPost) === null || _f === void 0 ? void 0 : _f.link));
    const [formCompressed, setFormCompressed] = useState(!props.isEditMode);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    // Polling state for new posts
    const [isPolling, setIsPolling] = useState(false);
    const [pollingMessage, setPollingMessage] = useState("");
    const hiddenFileInput = useRef(null);
    // Store the post IDs to add after user clicks OK
    const [pendingPostIds, setPendingPostIds] = useState([]);
    // Store existing media URL for preview
    const [existingMediaUrl, setExistingMediaUrl] = useState("");
    const [existingIsVideo, setExistingIsVideo] = useState(false);
    // Force re-render when media URLs are loaded
    const [forceRender, setForceRender] = useState(0);
    // Track if any changes have been made for edit mode
    const [hasChanges, setHasChanges] = useState(false);
    // URL auto-detection state
    const [autoDetectedUrl, setAutoDetectedUrl] = useState(null);
    // File uploader hook
    const fileUploader = useFileUploader({
        maxImageSize: 50 * 1024 * 1024,
        maxVideoSize: 1024 * 1024 * 1024,
        acceptedFileTypes: [
            "image/jpeg",
            "image/png",
            "video/mp4",
            "video/quicktime",
        ],
        onUploadError: (error) => {
            logger.error(LOGPREFIX + "Upload error: " + error.message);
            setErrorMessage(error.message);
            setUploadingFiles(false);
        },
    });
    // Channel posts store
    const channelPostsStore = useChannelPostsStore();
    // Handle completion of post creation - with polling for new posts
    const handlePostComplete = () => {
        // Add any pending post IDs before cleaning up
        if (props.addPostId && pendingPostIds.length > 0) {
            pendingPostIds.forEach((postId) => {
                var _a;
                (_a = props.addPostId) === null || _a === void 0 ? void 0 : _a.call(props, postId);
            });
        }
        // Clean up blob URLs to prevent memory leaks
        if (previewImageDataUrl && previewImageDataUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewImageDataUrl);
        }
        // Clean up state
        setPostTitle("");
        setPostSubtitle("");
        setPostText("");
        setPostLink("");
        setTempFiles([]);
        setPreviewImageDataUrl("");
        setDraftPostId(undefined);
        setPendingPostIds([]);
        setFormCompressed(true); // Collapse the form
        // Increment cache buster after successful post creation
        // cacheBusterStore.incrementCacheBuster();
        // Trigger React state update to reload posts smoothly
        console.log(LOGPREFIX +
            "handlePostComplete: Triggering React reload to show new post");
        console.log(LOGPREFIX + "handlePostComplete: reloading...");
        props.reload();
    };
    // Enhanced polling that waits for both post appearance AND image conversion
    const pollForNewPost = (postIds) => {
        console.log(LOGPREFIX +
            "pollForNewPost: ENTRY - checking " +
            postIds.length +
            " posts");
        if (!postIds.length) {
            console.log(LOGPREFIX + "pollForNewPost: No post IDs to check, completing");
            handlePostComplete();
            return;
        }
        const hasImages = tempFiles.length > 0 &&
            tempFiles.some((f) => f.fileType === "jpg" || f.fileType === "png");
        setIsPolling(true);
        setPollingMessage(hasImages
            ? "Waiting for post and image conversion..."
            : "Waiting for post to appear in channel...");
        const POLLING_INTERVAL = 4000; // 4 seconds
        const MAX_ATTEMPTS = hasImages ? 30 : 20; // Longer timeout for posts with images (120s vs 80s)
        let attempts = 0;
        // No progress interval needed - using indeterminate progress bar
        const pollInterval = setInterval(() => __awaiter(void 0, void 0, void 0, function* () {
            attempts++;
            console.log(LOGPREFIX + `pollForNewPost: Attempt ${attempts}/${MAX_ATTEMPTS}`);
            // Update polling message
            if (attempts <= 5) {
                setPollingMessage("Checking channel entries for new post...");
            }
            else if (attempts <= 12 && !hasImages) {
                setPollingMessage("Still waiting for post to appear in feed...");
            }
            else if (attempts <= 12 && hasImages) {
                setPollingMessage("Waiting for image conversion to complete...");
            }
            else if (attempts <= 20 && hasImages) {
                setPollingMessage("Processing images - this may take a moment...");
            }
            else {
                setPollingMessage("This is taking longer than expected...");
            }
            try {
                // Load channel posts with fresh cache buster
                yield channelPostsStore.loadChannelPosts(props.channelOwnerId, props.channelId, props.auth, cacheBusterStore, logger);
                // Check if any of our new post IDs appear in the channel's post list
                const expectedPostIds = postIds.map((id) => id.replace(".json", ""));
                let foundPost = false;
                let foundPostId = "";
                for (const expectedId of expectedPostIds) {
                    if (channelPostsStore.hasPostId(expectedId)) {
                        foundPost = true;
                        foundPostId = expectedId;
                        console.log(LOGPREFIX +
                            `pollForNewPost: Found post ${expectedId} in channel post list`);
                        break;
                    }
                }
                // Image readiness gate: when the post itself has appeared but the
                // server-side resize hasn't produced the `_512.jpg` thumbnail yet,
                // we keep polling rather than collapsing the form. This branch must
                // still respect MAX_ATTEMPTS — otherwise a post that's found but
                // whose image never becomes ready (e.g., multi-image upload where
                // the savePost lambda overwrites the temp key, or a slow/failed
                // resize) would leave the form open forever.
                let imageReady = !hasImages || !foundPost;
                if (foundPost && hasImages) {
                    console.log(LOGPREFIX +
                        "pollForNewPost: Post found, checking image conversion...");
                    // For multiple images, files are named _1_512.jpg, _2_512.jpg, etc.
                    // For single images, files are named _512.jpg
                    const imageUrlSuffix = tempFiles.length > 1 ? "_1_512.jpg" : "_512.jpg";
                    const postImageUrl = `${getContentURL()}/u/${props.channelOwnerId}/${props.channelId}/${foundPostId}${imageUrlSuffix}`;
                    const imageCheckUrl = props.auth.bearer
                        ? `${postImageUrl}?bearer=${encodeURIComponent(props.auth.bearer)}`
                        : postImageUrl;
                    try {
                        const imageResponse = yield fetch(imageCheckUrl, {
                            method: "HEAD",
                        });
                        imageReady = imageResponse.ok;
                        console.log(LOGPREFIX +
                            `pollForNewPost: Image check - status: ${imageResponse.status}, ready: ${imageReady}`);
                        if (!imageReady) {
                            console.log(LOGPREFIX +
                                "pollForNewPost: Image not ready yet, will continue polling unless max attempts reached");
                        }
                    }
                    catch (error) {
                        console.log(LOGPREFIX +
                            `pollForNewPost: Image check failed: ${error}, will continue polling unless max attempts reached`);
                        imageReady = false;
                    }
                }
                if (foundPost && imageReady) {
                    console.log(LOGPREFIX +
                        "pollForNewPost: Post found and ready! Redirecting to channel page...");
                    clearInterval(pollInterval);
                    setIsPolling(false);
                    setPollingMessage(hasImages
                        ? "Images ready! Reloading..."
                        : "Post found! Redirecting to channel page...");
                    // Clean up state before redirect
                    if (props.addPostId && pendingPostIds.length > 0) {
                        pendingPostIds.forEach((postId) => {
                            var _a;
                            (_a = props.addPostId) === null || _a === void 0 ? void 0 : _a.call(props, postId);
                        });
                    }
                    // Clean up blob URLs
                    if (previewImageDataUrl && previewImageDataUrl.startsWith("blob:")) {
                        URL.revokeObjectURL(previewImageDataUrl);
                    }
                    // Clear form state
                    setPostTitle("");
                    setPostSubtitle("");
                    setPostText("");
                    setPostLink("");
                    setTempFiles([]);
                    setPreviewImageDataUrl("");
                    setDraftPostId(undefined);
                    setPendingPostIds([]);
                    // Increment cache buster after successful post creation
                    // cacheBusterStore.incrementCacheBuster();
                    console.log(LOGPREFIX + "pollForNewPost: Reloading current page");
                    props.reload();
                }
                else if (attempts >= MAX_ATTEMPTS) {
                    console.log(LOGPREFIX +
                        "pollForNewPost: Max attempts reached, completing anyway");
                    clearInterval(pollInterval);
                    setIsPolling(false);
                    setPollingMessage(hasImages
                        ? "Images may still be processing, but your post has been created."
                        : "Taking longer than expected, but your post has been created.");
                    setTimeout(() => {
                        handlePostComplete();
                    }, 2000);
                }
                else {
                    console.log(LOGPREFIX +
                        `pollForNewPost: Post not yet found in channel post list (${channelPostsStore.postIds.length} posts checked)`);
                }
            }
            catch (error) {
                console.error(LOGPREFIX + `pollForNewPost: Error loading channel posts: ${error}`);
                if (attempts >= MAX_ATTEMPTS) {
                    console.log(LOGPREFIX +
                        "pollForNewPost: Max attempts reached after error, completing");
                    clearInterval(pollInterval);
                    setIsPolling(false);
                    setPollingMessage("Your post has been created.");
                    setTimeout(() => {
                        handlePostComplete();
                    }, 1000);
                }
            }
        }), POLLING_INTERVAL);
        console.log(LOGPREFIX +
            "pollForNewPost: Started polling using ChannelPostsStore" +
            (hasImages ? " with image conversion check" : ""));
    };
    // Generate new post ID
    const newPostId = () => {
        const timeIndex = 9999999999999 - new Date().getTime();
        const newPostId = timeIndex + "-" + uuidv4();
        return newPostId;
    };
    // Helper function to start file uploads.
    //
    // Multi-file uploads MUST go through fileUploader.uploadFiles, not a
    // Promise.all of uploadFile calls. The server's bearer/nonce check
    // (lambdas/bearer.ts:checkNonce) rejects a sequence reused more than
    // twice; parallel getUploadURL calls all carry the same un-rotated
    // bearer/seq and trip that check, dropping uploads. The batch helper
    // serializes the URL fetch (cheap), then fans out S3 PUTs in parallel.
    const startFileUploads = (newTempFiles, tempPartition, timestamp) => {
        // Extract metadata for all video files in the background — independent
        // of uploads; we just need it before publishDraft reads tempFiles.
        newTempFiles.forEach((tempFile, index) => __awaiter(void 0, void 0, void 0, function* () {
            if (tempFile.fileType === "mp4" || tempFile.fileType === "mov") {
                try {
                    const metadata = yield getVideoMetadata(tempFile.file);
                    logger.log(LOGPREFIX +
                        `Video metadata for file ${index}: ${JSON.stringify(metadata)}`);
                    setTempFiles((prev) => {
                        const updated = [...prev];
                        if (index < updated.length) {
                            updated[index] = Object.assign(Object.assign({}, updated[index]), { videoMetadata: metadata });
                        }
                        return updated;
                    });
                }
                catch (error) {
                    logger.error(LOGPREFIX +
                        `Failed to extract metadata for video ${index}: ${error}`);
                }
            }
        }));
        const batchInputs = newTempFiles.map((tempFile, i) => ({
            file: tempFile.file,
            contentId: `temp_${i}_${timestamp}`,
        }));
        logger.log(LOGPREFIX +
            `Starting batch upload of ${batchInputs.length} files to partition=${tempPartition}`);
        fileUploader
            .uploadFiles({
            files: batchInputs,
            partition: tempPartition,
            auth: props.auth,
            cacheBusterStore,
            onFileComplete: (index, result) => {
                setTempFiles((prev) => {
                    if (index >= prev.length)
                        return prev;
                    const updated = [...prev];
                    updated[index] = Object.assign(Object.assign({}, updated[index]), { uploadProgress: result.success ? 100 : 0, uploadComplete: result.success, uploadError: result.success ? undefined : result.error });
                    return updated;
                });
            },
        })
            .then((results) => {
            const successCount = results.filter((r) => r.success).length;
            const errorCount = results.length - successCount;
            logger.log(LOGPREFIX +
                `All uploads completed. Success: ${successCount}, Failed: ${errorCount}`);
            setUploadingFiles(false);
            setAllFilesUploaded(errorCount === 0);
        })
            .catch((error) => {
            logger.error(LOGPREFIX + "Batch upload error: " + error);
            setErrorMessage("Upload failed: " + (error.message || error));
            setUploadingFiles(false);
        });
    };
    // Handle file selection with immediate upload to temp storage
    const handleFileSelection = () => __awaiter(void 0, void 0, void 0, function* () {
        var _g, _h;
        if (((_g = hiddenFileInput.current) === null || _g === void 0 ? void 0 : _g.files) &&
            hiddenFileInput.current.files.length > 0) {
            const files = Array.from(hiddenFileInput.current.files);
            logger.log(LOGPREFIX + "handleFileSelection: " + files.length + " files selected");
            // Clear all error states immediately when new files are selected
            setErrorMessage(undefined);
            // Clear any existing temp file errors
            setTempFiles((prev) => prev.map((tf) => (Object.assign(Object.assign({}, tf), { uploadError: undefined }))));
            // Mark as having changes when new files are selected in edit mode
            if (props.isEditMode) {
                setHasChanges(true);
            }
            // Validate files
            const invalidFiles = files.filter((file) => file.type !== "image/jpeg" &&
                file.type !== "image/png" &&
                file.type !== "video/mp4" &&
                file.type !== "video/quicktime");
            if (invalidFiles.length > 0) {
                setErrorMessage("Unsupported file type(s): " +
                    invalidFiles.map((f) => f.type).join(", "));
                return;
            }
            // Ensure user is authenticated
            if (!props.auth.userId) {
                setErrorMessage("User ID not found. Please sign in again.");
                return;
            }
            // Generate temp path with timestamp to ensure uniqueness
            const timestamp = new Date().getTime();
            const tempPartition = `tmp/${props.auth.userId}/${timestamp}`;
            // Create TempFile entries
            const newTempFiles = files.map((file, index) => {
                // Determine file extension
                let fileType = "jpg"; // default
                if (file.type === "image/jpeg")
                    fileType = "jpg";
                else if (file.type === "image/png")
                    fileType = "png";
                else if (file.type === "video/mp4")
                    fileType = "mp4";
                else if (file.type === "video/quicktime")
                    fileType = "mov";
                // The actual S3 key will be: partition/contentId.fileType (same for both images and videos now)
                // We need to construct the exact S3 key that will be created
                const tempContentId = `temp_${index}_${timestamp}`;
                const actualS3Key = `${tempPartition}/${tempContentId}.${fileType}`;
                return {
                    file,
                    tempPath: actualS3Key,
                    filename: file.name,
                    fileType,
                    uploadProgress: 0,
                    uploadComplete: false,
                };
            });
            // Generate preview and extract metadata BEFORE updating state
            const firstFile = (_h = newTempFiles[0]) === null || _h === void 0 ? void 0 : _h.file;
            if (firstFile) {
                const isFirstVideo = isVideoFile(firstFile);
                if (!isFirstVideo) {
                    // Generate image preview immediately using blob URL - fast and memory-efficient!
                    logger.log(LOGPREFIX + "Generating image preview for file: " + firstFile.name);
                    try {
                        const previewUrl = URL.createObjectURL(firstFile);
                        logger.log(LOGPREFIX + "Image preview URL created: " + previewUrl);
                        // Update all state together to avoid race conditions
                        setPreviewImageDataUrl(previewUrl);
                        setTempFiles(newTempFiles);
                        setExistingMediaUrl("");
                        setExistingIsVideo(false);
                        setUploadingFiles(true);
                        setAllFilesUploaded(false);
                        // Force re-render to ensure preview updates correctly
                        setForceRender((prev) => prev + 1);
                        // Start the upload process immediately - no need to wait for preview
                        startFileUploads(newTempFiles, tempPartition, timestamp);
                    }
                    catch (error) {
                        logger.error(LOGPREFIX + "Failed to create preview URL: " + error);
                        // Still proceed with upload even if preview fails
                        setTempFiles(newTempFiles);
                        setExistingMediaUrl("");
                        setExistingIsVideo(false);
                        setPreviewImageDataUrl("");
                        setUploadingFiles(true);
                        setAllFilesUploaded(false);
                        setForceRender((prev) => prev + 1);
                        startFileUploads(newTempFiles, tempPartition, timestamp);
                    }
                }
                else if (isFirstVideo && canvasRef.current) {
                    // For videos, clear existing media and set state immediately
                    setTempFiles(newTempFiles);
                    setExistingMediaUrl("");
                    setExistingIsVideo(false);
                    setPreviewImageDataUrl("");
                    setUploadingFiles(true);
                    setAllFilesUploaded(false);
                    setForceRender((prev) => prev + 1);
                    // Extract video metadata
                    getVideoMetadata(firstFile)
                        .then((metadata) => {
                        logger.log(LOGPREFIX +
                            "Video metadata extracted: " +
                            JSON.stringify(metadata));
                        // Update the temp file with metadata
                        setTempFiles((prev) => {
                            const updated = [...prev];
                            if (updated.length > 0) {
                                updated[0] = Object.assign(Object.assign({}, updated[0]), { videoMetadata: metadata });
                            }
                            return updated;
                        });
                    })
                        .catch((error) => {
                        logger.error(LOGPREFIX + "Failed to extract video metadata: " + error);
                    });
                    // Generate thumbnail (but don't set it as preview for videos)
                    generateVideoThumbnail(firstFile, canvasRef.current, () => {
                        logger.log(LOGPREFIX + "Video thumbnail generated");
                        // Don't set preview image for videos - we show video processing image instead
                    }, (error) => {
                        logger.error(LOGPREFIX + "Video thumbnail generation failed: " + error);
                    });
                    // Start upload for videos
                    startFileUploads(newTempFiles, tempPartition, timestamp);
                }
            }
            else {
                // No files selected, clear state
                setTempFiles([]);
                setExistingMediaUrl("");
                setExistingIsVideo(false);
                setPreviewImageDataUrl("");
                setForceRender((prev) => prev + 1);
            }
        }
    });
    // Execute post save with reCAPTCHA token
    const doPublishWithToken = (token) => {
        var _a;
        const bearer = props.auth.bearer;
        const sequence = props.auth.sequence;
        if (!bearer || !sequence) {
            setErrorMessage("Authentication required");
            setSaving(false);
            return;
        }
        // Generate post ID for new posts
        let postId = draftPostId;
        if (!postId && !props.isEditMode) {
            postId = newPostId();
            setDraftPostId(postId);
        }
        // Prepare savePost input with reCAPTCHA token
        const savePostInput = {
            operation: "savePost",
            userId: props.auth.userId,
            channelOwnerId: props.channelOwnerId,
            channelId: props.channelId,
            postData: Object.assign(Object.assign({ title: postTitle, subtitle: postSubtitle, text: postText, link: postLink }, (props.studioJobId && { studioJobId: props.studioJobId })), (props.isEditMode &&
                ((_a = props.existingPost) === null || _a === void 0 ? void 0 : _a.postId) && {
                postId: props.existingPost.postId,
                isEdit: true,
            })),
            tempFiles: tempFiles.map((tf) => (Object.assign({ tempPath: tf.tempPath, filename: tf.filename, fileType: tf.fileType }, (tf.videoMetadata && {
                videoMetadata: {
                    width: tf.videoMetadata.width,
                    height: tf.videoMetadata.height,
                    orientation: tf.videoMetadata.orientation,
                    duration: tf.videoMetadata.duration,
                },
            })))),
            bearer,
            sequence,
            recaptchaToken: token,
        };
        const callbacks = {
            onSuccess: (result) => {
                // Update auth with new bearer/sequence
                if (result.bearer && result.sequence) {
                    props.auth.setBearerAndSequence(result.bearer, result.sequence, logger);
                }
                logger.log(LOGPREFIX + `Saved ${result.postIds.length} posts successfully`);
                setSaving(false);
                // Store post IDs and start polling
                if (result.postIds.length > 0) {
                    const postIdsWithJson = result.postIds.map((id) => id + ".json");
                    setPendingPostIds(postIdsWithJson);
                    pollForNewPost(postIdsWithJson);
                }
                else {
                    handlePostComplete();
                }
            },
            onError: (errorMessage) => {
                setErrorMessage(errorMessage);
                setSaving(false);
            },
        };
        savePost(savePostInput, callbacks, cacheBusterStore, logger);
    };
    // Publish using unified savePost operation with reCAPTCHA
    const publishDraft = () => __awaiter(void 0, void 0, void 0, function* () {
        var _j, _k;
        setSaving(true);
        setErrorMessage(undefined);
        const grecaptcha = window.grecaptcha;
        if (grecaptcha &&
            ((_j = grecaptcha.enterprise) === null || _j === void 0 ? void 0 : _j.ready) &&
            ((_k = grecaptcha.enterprise) === null || _k === void 0 ? void 0 : _k.execute)) {
            grecaptcha.enterprise.ready(() => __awaiter(void 0, void 0, void 0, function* () {
                grecaptcha.enterprise
                    .execute("6LeMTvYqAAAAAEXBRStAkzbH267Azriet8mmhqMq", {
                    action: "submit",
                })
                    .then((token) => {
                    doPublishWithToken(token);
                });
            }));
        }
        else {
            doPublishWithToken("grecaptcha-not-loaded");
        }
    });
    // Validation functions
    const validatePostTitle = (title) => {
        if (!title) {
            setPostTitleHelper(undefined);
            return;
        }
        if (title.includes("<")) {
            setPostTitleHelper("Title cannot contain '<' character.");
        }
        else if (title.includes("\n")) {
            setPostTitleHelper("Title cannot contain newline character.");
        }
        else {
            setPostTitleHelper(undefined);
        }
    };
    const validatePostLink = (link) => {
        if (!link) {
            setPostLinkHelper(undefined);
            return;
        }
        if (!link.startsWith("https://")) {
            setPostLinkHelper("External Link URL must start with 'https://'");
        }
        else {
            setPostLinkHelper(undefined);
        }
    };
    const validatePostSubtitle = (subtitle) => {
        if (!subtitle) {
            setPostSubtitleHelper(undefined);
            return;
        }
        if (subtitle.includes("<")) {
            setPostSubtitleHelper("Subtitle cannot contain '<' character.");
        }
        else if (subtitle.includes("\n")) {
            setPostSubtitleHelper("Subtitle cannot contain newline character.");
        }
        else {
            setPostSubtitleHelper(undefined);
        }
    };
    const validatePostText = (text) => {
        if (!text) {
            setPostTextHelper(undefined);
            return;
        }
        if (text.includes("<")) {
            setPostTextHelper("Text cannot contain '<' character.");
        }
        else {
            setPostTextHelper(undefined);
        }
    };
    // Effect hooks for validation
    useEffect(() => {
        validatePostTitle(postTitle);
    }, [postTitle]);
    useEffect(() => {
        validatePostSubtitle(postSubtitle);
    }, [postSubtitle]);
    useEffect(() => {
        validatePostText(postText);
    }, [postText]);
    useEffect(() => {
        validatePostLink(postLink);
    }, [postLink]);
    // URL auto-detection effect
    useEffect(() => {
        if (!postText) {
            // Clear auto-detected URL if text is empty
            setAutoDetectedUrl(null);
            return;
        }
        const detectedUrl = extractFirstUrl(postText);
        if (detectedUrl && detectedUrl !== autoDetectedUrl) {
            // Only auto-populate if link field is empty or contains the previous auto-detected URL
            const shouldAutoPopulate = !postLink || postLink === autoDetectedUrl;
            if (shouldAutoPopulate) {
                logger.log(LOGPREFIX + "Auto-detected URL: " + detectedUrl);
                setPostLink(detectedUrl);
                setAddLinkVisible(true);
                setAutoDetectedUrl(detectedUrl);
            }
            else {
                // User has manually entered a different URL, just update our tracking
                setAutoDetectedUrl(detectedUrl);
            }
        }
        else if (!detectedUrl &&
            autoDetectedUrl &&
            postLink === autoDetectedUrl) {
            // URL was removed from text and link field still has the auto-detected URL
            setPostLink("");
            setAutoDetectedUrl(null);
        }
    }, [postText, autoDetectedUrl, postLink, logger]);
    // Monitor temp files to update allFilesUploaded state
    useEffect(() => {
        if (tempFiles.length > 0) {
            const allUploaded = tempFiles.every((tf) => tf.uploadComplete === true);
            const hasErrors = tempFiles.some((tf) => tf.uploadError);
            logger.log(LOGPREFIX +
                `Checking upload status: ${tempFiles.length} files, all uploaded: ${allUploaded}, has errors: ${hasErrors}`);
            setAllFilesUploaded(allUploaded && !hasErrors);
        }
    }, [tempFiles]);
    // Track changes in edit mode
    useEffect(() => {
        if (props.isEditMode && props.existingPost) {
            const titleChanged = postTitle !== (props.existingPost.title || "");
            const subtitleChanged = postSubtitle !== (props.existingPost.subtitle || "");
            const textChanged = postText !== (props.existingPost.text || "");
            const linkChanged = postLink !== (props.existingPost.link || "");
            const mediaChanged = tempFiles.length > 0;
            const anyChanges = titleChanged ||
                subtitleChanged ||
                textChanged ||
                linkChanged ||
                mediaChanged;
            setHasChanges(anyChanges);
        }
    }, [
        postTitle,
        postSubtitle,
        postText,
        postLink,
        tempFiles.length,
        props.isEditMode,
        props.existingPost,
    ]);
    // Load existing media for preview when editing
    useEffect(() => {
        logger.log(LOGPREFIX +
            "Checking existing media - isEditMode: " +
            props.isEditMode +
            ", existingPost: " +
            (props.existingPost ? "yes" : "no") +
            ", tempFiles: " +
            tempFiles.length);
        if (props.isEditMode && props.existingPost && !tempFiles.length) {
            logger.log(LOGPREFIX +
                "Loading existing media for post: " +
                props.existingPost.postId);
            logger.log(LOGPREFIX +
                "Post hasImage: " +
                props.existingPost.hasImage +
                ", hasVideo: " +
                props.existingPost.hasVideo +
                ", hasMultipleFiles: " +
                props.existingPost.hasMultipleFiles +
                ", multipleFileCount: " +
                props.existingPost.multipleFileCount);
            const postUrl = `${getContentURL()}/u/${props.channelOwnerId}/${props.channelId}/${props.existingPost.postId}`;
            // Add bearer token for authentication if available
            const bearerParam = props.auth.bearer
                ? `?bearer=${props.auth.bearer}`
                : "";
            if (props.existingPost.hasVideo) {
                // For video posts, show the video with HLS format
                const videoBaseUrl = `${postUrl}.mp4`;
                const hlsVideoUrl = getConvertedVideoUrl(videoBaseUrl);
                const videoUrl = `${hlsVideoUrl}${bearerParam}`;
                logger.log(LOGPREFIX + "Setting HLS video URL: " + videoUrl);
                setExistingMediaUrl(videoUrl);
                setExistingIsVideo(true);
                // Force re-render to display the media
                setForceRender((prev) => prev + 1);
            }
            else if (props.existingPost.hasImage) {
                // For image posts, show the image
                let imageUrl;
                if (props.existingPost.hasMultipleFiles &&
                    props.existingPost.multipleFileCount) {
                    // For multiple files, show the first image
                    imageUrl = `${postUrl}_1_512.jpg${bearerParam}`;
                }
                else {
                    // For single image
                    imageUrl = `${postUrl}_512.jpg${bearerParam}`;
                }
                logger.log(LOGPREFIX + "Setting image URL: " + imageUrl);
                setExistingMediaUrl(imageUrl);
                setExistingIsVideo(false);
                // Force re-render to display the media
                setForceRender((prev) => prev + 1);
            }
            else {
                logger.log(LOGPREFIX + "Post has no media to display");
            }
        }
    }, [
        props.isEditMode,
        props.existingPost,
        props.channelOwnerId,
        props.channelId,
        tempFiles.length,
        props.auth.userId,
    ]);
    // Load studio job data when studioJobId is provided
    useEffect(() => {
        console.log("NewPostCard: studioJobId prop =", props.studioJobId);
        if (!studioJobLoaded && props.studioJobId && !loadingStudioJob) {
            logger.log(LOGPREFIX + "Loading studio job: " + props.studioJobId);
            setLoadingStudioJob(true);
            setStudioJobLoaded(true);
            studioJobStore
                .getJob(props.studioJobId, props.auth, cacheBusterStore, logger)
                .then((job) => {
                logger.log(LOGPREFIX +
                    "Studio job loaded: " +
                    job.jobId +
                    " = " +
                    JSON.stringify(job));
                // Set the post title from studio job
                if (job.title) {
                    setPostTitle(job.title);
                }
                else if (job.positivePrompt) {
                    // Use first 50 characters of prompt as title
                    setPostTitle(job.positivePrompt.substring(0, 50) +
                        (job.positivePrompt.length > 50 ? "..." : ""));
                }
                setFormCompressed(false);
                // Create a temp file from the studio job result
                if (job.jobStatus === "completed" &&
                    job.jobFiles &&
                    job.jobFiles.length > 0) {
                    console.log(LOGPREFIX + "Job completed and has job files.");
                    setStudioJob(job);
                }
            })
                .catch((error) => {
                logger.error(LOGPREFIX + "Error loading studio job: " + error.message);
            })
                .finally(() => {
                setLoadingStudioJob(false);
            });
        }
    }, [props.studioJobId, props.auth.isSignedIn(), loadingStudioJob]);
    // Render compressed form for new posts
    if (formCompressed &&
        !props.isEditMode &&
        props.auth &&
        props.auth.isSignedIn() &&
        props.auth.userId &&
        props.auth.displayName) {
        return (_jsx(Card, Object.assign({ sx: {
                backgroundColor: "#FFFFFF",
                color: "black",
                marginTop: "20px",
                marginBottom: "20px",
                width: "100%",
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
            } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "10px !important" } }, { children: _jsxs(Stack, Object.assign({ direction: "row", sx: {
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                    } }, { children: [_jsx(UserAvatar, { keyStr: "add-post-avatar", userId: props.auth.userId, userName: props.auth.displayName, onUserClicked: () => {
                                navigate("/Profile");
                            }, userNameVisible: false, auth: props.auth }), _jsx(Stack, Object.assign({ sx: {
                                flex: 1,
                                backgroundColor: "#F5F5F5",
                                borderRadius: "20px",
                                padding: "8px 16px",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "#EEEEEE",
                                },
                            }, onClick: () => {
                                setFormCompressed(false);
                            } }, { children: _jsx(Typography, Object.assign({ variant: "body1", color: "#888888" }, { children: "Post something..." })) }))] }), "add-post-button-stack") })) }), props.keyStr + ".editable.compressed"));
    }
    // Main form content
    const cardContent = (_jsxs(Stack, Object.assign({ style: { padding: "10px" } }, { children: [!saving && !isPolling && (_jsxs(_Fragment, { children: [_jsx(TextField, { fullWidth: true, label: "Title", color: "black", size: "small", value: postTitle, onChange: (e) => setPostTitle(e.target.value), error: !!postTitleHelper, helperText: postTitleHelper, disabled: uploadingFiles, sx: { marginBottom: "8px" } }), _jsx(TextField, { fullWidth: true, label: "Subtitle", color: "black", size: "small", value: postSubtitle, onChange: (e) => setPostSubtitle(e.target.value), error: !!postSubtitleHelper, helperText: postSubtitleHelper, disabled: uploadingFiles, sx: { marginBottom: "8px" } }), _jsx(TextField, { fullWidth: true, label: "Write your post here...", multiline: true, rows: 2, color: "black", value: postText, onChange: (e) => setPostText(e.target.value), error: !!postTextHelper, helperText: postTextHelper, disabled: uploadingFiles, sx: { marginBottom: addLinkVisible ? "8px" : "3px" } }), addLinkVisible && (_jsx(TextField, { fullWidth: true, label: "External Link URL", color: "black", size: "small", value: postLink, onChange: (e) => setPostLink(e.target.value), error: !!postLinkHelper, helperText: postLinkHelper, disabled: uploadingFiles, sx: { marginBottom: "8px" } }))] })), _jsxs(Stack, Object.assign({ direction: "row", width: "100%", sx: { marginTop: "5px" }, spacing: 1, style: { display: "flex", justifyContent: "center" } }, { children: [_jsx("input", { type: "file", accept: "image/jpeg,image/png,video/mp4,video/quicktime", multiple: true, ref: hiddenFileInput, style: { display: "none" }, onChange: handleFileSelection }), _jsx("canvas", { ref: canvasRef, style: { display: "none" } }), !saving && !uploadingFiles && !isPolling && (_jsxs(_Fragment, { children: [!studioJob && (_jsx(Button, Object.assign({ variant: tempFiles.length > 0 ? "outlined" : "contained", endIcon: _jsx(AddAPhoto, {}), onClick: () => { var _a; return (_a = hiddenFileInput.current) === null || _a === void 0 ? void 0 : _a.click(); }, "aria-label": "Add or Replace Photos and Videos" }, { children: tempFiles.length > 0
                                    ? tempFiles.length > 1
                                        ? `REPLACE MEDIA (${tempFiles.length} files)`
                                        : "REPLACE MEDIA"
                                    : "+ PHOTOS/VIDEOS" }))), _jsx(Button, Object.assign({ variant: postLink || studioJob ? "outlined" : "contained", endIcon: _jsx(AddLink, {}), onClick: () => setAddLinkVisible(true) }, { children: "+ LINK" }))] }))] })), (uploadingFiles || (tempFiles.length > 0 && allFilesUploaded)) && (_jsxs(Stack, Object.assign({ spacing: 1, sx: { marginTop: "10px" } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: allFilesUploaded ? "success.main" : "primary" }, { children: allFilesUploaded
                            ? "Files Uploaded - Ready to Publish"
                            : "Uploading files..." })), tempFiles.map((tf, index) => (_jsxs(Stack, Object.assign({ spacing: 0.5 }, { children: [_jsxs(Typography, Object.assign({ variant: "caption", color: tf.uploadComplete
                                    ? "success.main"
                                    : tf.uploadError
                                        ? "error"
                                        : "text.secondary" }, { children: [tf.file.name, " ", tf.uploadComplete ? "✓" : tf.uploadError ? "✗" : "..."] })), !tf.uploadComplete && !tf.uploadError && _jsx(LinearProgress, {}), tf.uploadError && (_jsx(Typography, Object.assign({ variant: "caption", color: "error" }, { children: tf.uploadError })))] }), index)))] }))), (saving || isPolling) && (_jsx(Card, Object.assign({ elevation: 0, sx: {
                    backgroundColor: "rgba(31, 0, 238, 0.04)",
                    padding: "4",
                    marginTop: "0.5em",
                } }, { children: _jsxs(CardContent, Object.assign({ sx: { textAlign: "center" } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "primary", gutterBottom: true }, { children: saving
                                ? props.isEditMode
                                    ? "SAVING POST"
                                    : "PUBLISHING POST"
                                : "UPDATING CONTENT" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { marginBottom: "15px" } }, { children: saving
                                ? props.isEditMode
                                    ? "Saving your changes..."
                                    : "Publishing your post..."
                                : pollingMessage })), _jsx(LinearProgress, { color: "primary", variant: "indeterminate", sx: {
                                height: 8,
                                borderRadius: 4,
                            } }, "new-post-saving-progress")] })) }))), errorMessage && (_jsx(Card, Object.assign({ elevation: 0, sx: { backgroundColor: "#FFEEEE", padding: "4", marginTop: "0.5em" } }, { children: _jsxs(CardContent, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "error", gutterBottom: true }, { children: "ERROR" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: errorMessage }))] }) })))] }), props.keyStr + errorMessage));
    // Preview section - show video processing image for videos
    const hasVideo = tempFiles.some((f) => f.fileType === "mp4" || f.fileType === "mov");
    // Create header similar to PostCard
    const previewHeader = postTitle || postSubtitle ? (_jsx(CardHeader, { title: postTitle, subheader: postSubtitle, avatar: _jsx(UserAvatar, { keyStr: "preview.avatar", userId: props.auth.userId || "", userName: props.auth.displayName || "", userNameVisible: false, auth: props.auth, onUserClicked: () => { } }), titleTypographyProps: {
            variant: "h6",
            margin: "0",
            marginRight: "18px",
            marginTop: postSubtitle ? "0" : "6px",
            marginBottom: postSubtitle ? "0" : "6px",
            padding: "0",
        }, subheaderTypographyProps: {
            variant: "body1",
            margin: "0",
            marginRight: "17px",
            padding: "0",
        }, sx: {
            margin: 0,
            padding: 0,
            marginLeft: "10px",
            marginRight: "10px",
        } })) : undefined;
    // Process link for display (same logic as PostCard)
    let linkName;
    if (postLink && postLink.length > 8) {
        const parts = postLink.substring(8, postLink.length);
        linkName = parts.split("/")[0];
    }
    // Determine what to show in preview
    let previewMediaUrl = "";
    let previewIsVideo = false;
    // Priority 1: If we have new files uploaded or being processed
    if (tempFiles.length > 0 || uploadingFiles) {
        if (hasVideo) {
            // For videos, we show the video processing placeholder (no URL needed)
            previewMediaUrl = "";
            previewIsVideo = true;
        }
        else if (previewImageDataUrl) {
            // For images, show the preview image
            previewMediaUrl = previewImageDataUrl;
            previewIsVideo = false;
        }
        else {
            // Files selected but preview not ready yet - don't fall back to existing media
            previewMediaUrl = "";
            previewIsVideo = false;
        }
    }
    // Priority 2: If no new files but we have existing media (edit mode)
    else if (existingMediaUrl) {
        previewMediaUrl = existingMediaUrl;
        previewIsVideo = existingIsVideo;
    }
    else if (studioJob &&
        studioJobLoaded &&
        studioJob.jobFiles &&
        studioJob.jobFiles.length > 0) {
        const resultFile = studioJob.jobFiles[0];
        previewIsVideo =
            studioJob.jobType === "text-to-video" ||
                studioJob.jobType === "image-to-video";
        // Set preview URL for display
        const studioFileUrl = `${getContentURL()}/${resultFile}`;
        const bearerParam = props.auth.bearer ? `?bearer=${props.auth.bearer}` : "";
        previewMediaUrl = `${studioFileUrl}${bearerParam}`;
        console.log(LOGPREFIX + "Preview Media URL = " + previewMediaUrl);
    }
    // Helper function to create child component with text and link
    const createPreviewChildComponent = (includeVideoProcessing = false) => {
        return (_jsxs(Stack, Object.assign({ direction: "column" }, { children: [includeVideoProcessing && (_jsxs(Stack, Object.assign({ alignItems: "center", justifyContent: "center", sx: {
                        width: "100%",
                        padding: 2,
                    } }, { children: [_jsx("img", { src: "/img/video-processing.jpg", alt: "Video Processing", style: {
                                maxWidth: "100%",
                                maxHeight: window.innerHeight - 500,
                                height: "auto",
                                borderRadius: "4px",
                            } }), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { mt: 1 } }, { children: "Video will be processed after upload" }))] }))), linkName && (_jsx(Link, Object.assign({ href: postLink, underline: "none", fontSize: "medium", target: "_blank", sx: {
                        textAlign: "center",
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        display: "block",
                    } }, { children: linkName }))), postText && (_jsx("div", Object.assign({ style: {
                        textAlign: "left",
                        paddingTop: linkName ? "0px" : "5px",
                        paddingBottom: "5px",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                    } }, { children: _jsx(Typography, Object.assign({ variant: "body1", color: "black", component: "div", sx: {
                            wordBreak: "break-word",
                            whiteSpace: "pre-line",
                        } }, { children: renderTextWithStyledUrls(postText, getFirstUrlFromText(postText)
                            ? () => {
                                // If the preview text contains URLs, open the first one in a new tab
                                const firstUrl = getFirstUrlFromText(postText);
                                if (firstUrl) {
                                    window.open(firstUrl, "_blank");
                                }
                            }
                            : undefined) })) })))] }), "preview-content-stack"));
    };
    const previewContent = previewMediaUrl && !previewIsVideo ? (_jsx(ContentCard, { title: "", header: previewHeader, childComponent: createPreviewChildComponent(), mediaUrl: previewMediaUrl, mediaIsVideo: false, keyStr: props.keyStr + ".preview.image", textColor: "black", backgroundColor: "#FFFFFF", maxMediaWidth: window.innerWidth - 100, maxMediaHeight: window.innerHeight - 400 })) : hasVideo ? (_jsx(ContentCard, { title: "", header: previewHeader, childComponent: createPreviewChildComponent(true), keyStr: props.keyStr + ".preview.video.new", textColor: "black", backgroundColor: "#FFFFFF", maxMediaWidth: window.innerWidth - 100, maxMediaHeight: window.innerHeight - 400 })) : previewMediaUrl && existingIsVideo ? (
    // Show existing video
    _jsx(ContentCard, { title: "", header: previewHeader, childComponent: createPreviewChildComponent(), mediaUrl: previewMediaUrl, mediaIsVideo: true, keyStr: props.keyStr + ".preview.existing.video." + forceRender, textColor: "black", backgroundColor: "#FFFFFF", maxMediaWidth: window.innerWidth - 100, maxMediaHeight: window.innerHeight - 400 })) : postTitle || postSubtitle || postText || linkName ? (_jsx(ContentCard, { title: "", header: previewHeader, childComponent: createPreviewChildComponent(), keyStr: props.keyStr + ".preview.text", textColor: "black", backgroundColor: "#FFFFFF" })) : null;
    // Render main card
    const isValid = !postTitleHelper &&
        !postSubtitleHelper &&
        !postTextHelper &&
        !postLinkHelper &&
        (postTitle.length > 0 || tempFiles.length > 0); // Allow posts with files even without title
    // For edit mode, also require changes to enable save
    const canSave = props.isEditMode ? isValid && hasChanges : isValid;
    return (_jsxs(Card, Object.assign({ sx: {
            backgroundColor: "#FFFFFF",
            color: "black",
            marginTop: "20px",
            marginBottom: "20px",
            width: "100%",
            boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
        } }, { children: [_jsx(CardHeader, { title: _jsx(Typography, Object.assign({ variant: "h6", sx: { fontWeight: 500 } }, { children: props.isEditMode ? "Edit Post" : "Create New Post" })), sx: { textAlign: "center", paddingBottom: 0 } }), previewContent && (_jsxs(CardContent, Object.assign({ sx: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", sx: {
                            marginBottom: "10px",
                            alignSelf: "flex-start",
                            width: "100%",
                        } }, { children: "Preview" })), _jsx("div", Object.assign({ style: { width: "100%", display: "flex", justifyContent: "center" } }, { children: previewContent }))] }))), _jsx(CardContent, { children: cardContent }), _jsxs(CardActions, Object.assign({ sx: {
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    paddingBottom: 2,
                } }, { children: [_jsxs(Stack, Object.assign({ direction: "row", spacing: 1, sx: { justifyContent: "center" } }, { children: [_jsx(Button, Object.assign({ "aria-label": "Publish Button", variant: "contained", color: "primary", endIcon: props.isEditMode ? _jsx(SaveAlt, {}) : _jsx(IosShare, {}), onClick: publishDraft, disabled: !canSave ||
                                    saving ||
                                    uploadingFiles ||
                                    isPolling ||
                                    (tempFiles.length > 0 && !allFilesUploaded), sx: { minWidth: "150px" } }, { children: props.isEditMode ? "Save Post" : "Publish" })), props.isEditMode && props.onCancel && (_jsx(Button, Object.assign({ "aria-label": "Cancel Button", variant: "outlined", onClick: props.onCancel, disabled: saving || uploadingFiles, sx: { minWidth: "100px" } }, { children: "Cancel" })))] })), tempFiles.length > 0 && !allFilesUploaded && (_jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: "Wait for uploads to complete..." }))), !props.isEditMode && (_jsxs(Typography, Object.assign({ variant: "caption", color: "text.secondary", sx: { textAlign: "center", marginTop: 1 } }, { children: ["This site is protected by reCAPTCHA and the Google", " ", _jsx(Link, Object.assign({ href: "https://policies.google.com/privacy", target: "_blank", rel: "noopener" }, { children: "Privacy Policy" })), " ", "and", " ", _jsx(Link, Object.assign({ href: "https://policies.google.com/terms", target: "_blank", rel: "noopener" }, { children: "Terms of Service" })), " ", "apply."] })))] }))] }), props.keyStr + ".editable." + forceRender));
};
